import * as React from 'react';
import {connect} from 'react-redux';

import * as ramda from 'ramda';

import {
    convertToArray,
    safelyGetFilterByKey,
    safelyGetRelationshipFilterIdsFromFilterValue,
} from 'nutshell-core/filter';
import {getDateRangeFromDateFilter} from 'nutshell-core/get-date-range-from-date-filter';
import {Routing} from 'nutshell-core/routing';
import type {FilterObject} from 'nutshell-core/types';

import type {SelectMenuOption} from 'shells/action-menu/action-menu';

import {TitleBarActionButton} from '../base/title-bar/title-bar-action-button';

import * as activityReportActions from './activity/activity-report-actions';
import {downloadReport} from './download-report';
import {
    LEAD_LIST_EXPORT_KEY,
    REPORT_DATA_EXPORT_KEY,
    REPORT_CHART_EXPORT_KEY,
    REPORT_ACTIVITY_DETAILS_EXPORT_KEY,
    REPORT_ACTIVITY_OVERVIEW_EXPORT_KEY,
    REPORT_FUNNEL_EXPORT_KEY,
    getExportTypes,
    getReportTitle,
    CHART_HERO_DIV_ID,
    TASKS,
} from './report-constants';
import type {ReportKey, ReportURLParams, ExportKey} from './report-types';
import {getPostParams} from './report-utils';
import * as actions from './reports-actions';

import actionButtonStyles from '../base/title-bar/title-bar-action-button.module.css';

const REST_URL = '/rest/leads/report';
const TASKS_REST_URL = '/rest/tasks/report';

type OwnProps = {
    reportKey: ReportKey;
    urlParams: ReportURLParams<'leads'>;
};
type DispatchProps = {
    onExportLeadList: (
        filter: FilterObject,
        fields?: {
            [key: string]: string;
        }
    ) => any;
    onExportActivityDetail: () => any;
    onExportActivityActivities: () => any;
};
type Props = OwnProps & DispatchProps;

type State = {
    exportOptions: Array<SelectMenuOption>;
};

// Taken from https://stackoverflow.com/a/47487073
if (!HTMLCanvasElement.prototype.toBlob) {
    Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
        // @ts-expect-error
        value: function (callback, type, quality) {
            const canvas = this; // eslint-disable-line @typescript-eslint/no-this-alias
            setTimeout(function () {
                const binStr = atob(canvas.toDataURL(type, quality).split(',')[1]),
                    len = binStr.length,
                    arr = new Uint8Array(len);

                for (let i = 0; i < len; i++) {
                    arr[i] = binStr.charCodeAt(i);
                }

                callback(new Blob([arr], {type: type || 'image/png'}));
            });
        },
    });
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onExportLeadList: (filter, fields) => {
            // @ts-expect-error
            dispatch(actions.exportLeadList(filter, fields));
        },
        onExportActivityDetail: () => {
            dispatch(activityReportActions.exportActivityReportDetail());
        },
        onExportActivityActivities: () => {
            dispatch(activityReportActions.exportActivityReportActivities());
        },
    };
}

const getExportOptions = (props: Props, desiredExportKeys: Array<ExportKey>) => {
    let chartDataLink;

    if (desiredExportKeys.includes(REPORT_DATA_EXPORT_KEY)) {
        const getPostParamsCommonArgs = [
            props.reportKey,
            props.urlParams.report,
            convertToArray(props.urlParams.filter),
        ];

        if (desiredExportKeys.includes(REPORT_DATA_EXPORT_KEY)) {
            // @ts-expect-error
            const chartDataParams = getPostParams(...getPostParamsCommonArgs, 'csv');
            chartDataLink = `${
                props.reportKey === TASKS ? TASKS_REST_URL : REST_URL
            }?${Routing.param(chartDataParams)}`;
        }
    }

    const {canExportData} = NutClientConfig;
    const dateRangeFilter = safelyGetFilterByKey(
        convertToArray(props.urlParams.filter),
        props.urlParams.report ? props.urlParams.report.dateRangeType : ''
    );

    const options = [
        {
            label: (
                <a
                    className={actionButtonStyles['action-menu-item']}
                    href={chartDataLink}
                    download={true}
                    // @ts-expect-error
                    disabled={!chartDataLink}
                >
                    Download report summary
                </a>
            ),
            isDisabled: !chartDataLink,
            key: REPORT_DATA_EXPORT_KEY,
        },
        {
            label: 'Download report image',
            handleSelect: () =>
                downloadReport({
                    chartSelector: `#${CHART_HERO_DIV_ID} > div`,
                    reportTitle: `${getReportTitle(props.reportKey)} Report${getReportDuration(
                        dateRangeFilter
                    )}.png`,
                }),
            key: REPORT_CHART_EXPORT_KEY,
        },
        {
            label: 'Download lead list',
            isDisabled: false,
            key: LEAD_LIST_EXPORT_KEY,
            handleSelect: () => {
                let decodedFields;
                const fields = props.urlParams.list && props.urlParams.list.fields;
                if (fields) {
                    // This is needed to convert fields with spaces from "Company%2520field" to "Company field"
                    // @ts-expect-error
                    decodedFields = ramda.map((keyFields) => decodeURIComponent(keyFields), fields);
                }

                // @ts-expect-error
                props.onExportLeadList(props.urlParams.filter, decodedFields);
            },
        },
        {
            label: 'Download detail',
            isDisabled: false,
            key: REPORT_ACTIVITY_DETAILS_EXPORT_KEY,
            // We don't have to pass filters here because they're stored in
            // our activity report session since this is a 'legacy' report
            handleSelect: () => props.onExportActivityDetail(),
        },
        {
            label: 'Download activities',
            isDisabled: false,
            key: REPORT_ACTIVITY_OVERVIEW_EXPORT_KEY,
            // We don't have to pass filters here because they're stored in
            // our activity report session since this is a 'legacy' report
            handleSelect: () => props.onExportActivityActivities(),
        },
        {
            label: 'Download funnel report data',
            isDisabled: false,
            key: REPORT_FUNNEL_EXPORT_KEY,
            handleSelect: () => {
                const stagesetIdFilter = safelyGetFilterByKey(
                    convertToArray(props.urlParams.filter),
                    'stageset'
                );
                const stagesetId = stagesetIdFilter
                    ? safelyGetRelationshipFilterIdsFromFilterValue(stagesetIdFilter)[0]
                    : '';
                window.location.href = `/rest/stagesets/${stagesetId}/export`;
            },
        },
    ];

    return (
        options
            // @ts-expect-error
            .filter((opt) => desiredExportKeys.includes(opt.key))
            .filter((opt) => opt.key !== LEAD_LIST_EXPORT_KEY || canExportData)
    );
};

export class ReportActionButtonComponent extends React.PureComponent<Props, State> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            exportOptions: [],
        };
    }

    UNSAFE_componentWillMount() {
        const desiredExportKeys = getExportTypes(this.props.reportKey);

        this.setState({
            exportOptions: getExportOptions(this.props, desiredExportKeys),
        });
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (
            nextProps.reportKey !== this.props.reportKey ||
            nextProps.urlParams !== this.props.urlParams
        ) {
            const desiredExportKeys = getExportTypes(nextProps.reportKey);
            this.setState({
                exportOptions: getExportOptions(nextProps, desiredExportKeys),
            });
        }
    }

    render() {
        return (
            <TitleBarActionButton
                exportOptions={this.state.exportOptions}
                showNotification={true}
            />
        );
    }
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(undefined, mapDispatchToProps);

export const ReportActionButton = connector(ReportActionButtonComponent);

// @ts-expect-error
function getReportDuration(dateRangeFilter): string {
    const dateRange =
        typeof dateRangeFilter === 'string'
            ? getDateRangeFromDateFilter(
                  dateRangeFilter,
                  undefined,
                  typeof NutshellFrontendSettings !== 'undefined'
                      ? NutshellFrontendSettings.fiscal_year
                      : '1-1',
                  typeof NutshellFrontendSettings !== 'undefined'
                      ? NutshellFrontendSettings.work_week_start
                      : '7'
              )
            : undefined;

    if (!dateRange) return '';

    return ` ${dateRange.startDate.format('YYYYMMDD')}-${dateRange.endDate.format('YYYYMMDD')}`;
}
